import type { FloatParam, IntParam, Param } from './types';
import { paramBulkRange } from './types';

/**
 * Per-parameter exploration settings.
 *
 * One concept serves two consumers: the randomise buttons in the studio and the batch
 * engine both ask "what values may this parameter take?". Narrowing the range with the
 * double slider therefore has an immediate, visible effect — it constrains randomise
 * right away — and the same setting later drives batch generation.
 */
export type BulkDistribution = 'uniform' | 'normal' | 'exponential';

export interface ParamBulkSpec {
  /** Locked parameters are never touched by randomise or by the batch engine. */
  locked?: boolean;
  /** Numeric bounds; overrides the schema's `bulkRange`. */
  range?: [number, number];
  /** Allowed values for enum and text parameters. */
  values?: string[];
  distribution?: BulkDistribution;
  /** Spread of the normal distribution, as a fraction of the range. */
  sigma?: number;
}

export type LayerBulk = Record<string, ParamBulkSpec>;

export const DEFAULT_SIGMA = 0.25;

export function isLocked(bulk: LayerBulk | undefined, key: string): boolean {
  return bulk?.[key]?.locked === true;
}

/** The bounds a numeric parameter may be varied within, honouring user overrides. */
export function effectiveRange(param: FloatParam | IntParam, spec?: ParamBulkSpec): [number, number] {
  const fallback = paramBulkRange(param);
  const range = spec?.range ?? fallback;
  const lo = Math.max(param.min, Math.min(range[0], range[1]));
  const hi = Math.min(param.max, Math.max(range[0], range[1]));
  return lo <= hi ? [lo, hi] : [fallback[0], fallback[1]];
}

/** The values a discrete parameter may take, honouring user overrides. */
export function effectiveValues(param: Param, spec?: ParamBulkSpec): string[] {
  if (param.kind === 'enum') {
    const all = param.options.map((o) => o.value);
    const chosen = spec?.values?.filter((v) => all.includes(v));
    return chosen && chosen.length > 0 ? chosen : [...(param.bulkValues ?? all)];
  }
  if (param.kind === 'text') {
    const chosen = spec?.values?.filter((v) => v.length > 0);
    if (chosen && chosen.length > 0) return chosen;
    return [...(param.bulkValues ?? [])];
  }
  return [];
}

/** True when the user has narrowed a parameter away from the schema default. */
export function isNarrowed(param: Param, spec?: ParamBulkSpec): boolean {
  if (!spec) return false;
  if (spec.locked) return true;
  if (spec.values && spec.values.length > 0) return true;
  if (!spec.range) return false;
  if (param.kind !== 'float' && param.kind !== 'int') return false;
  const fallback = paramBulkRange(param);
  return spec.range[0] !== fallback[0] || spec.range[1] !== fallback[1];
}
